


































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import BaseModal from '@/jbi-shared/vue-components/BaseModal.vue';
import { cloneDeep as _cloneDeep, first } from 'lodash';
import {
  AuditSamplingSite,
  OneOffCalculateData,
} from '@/store/modules/audits/types/audits.types';
import { SamplingMode } from '@/jbi-shared/types/project.types';
import { CplusCriteria } from '@/store/modules/projects/types/projects.types';
import { distributeTotal, redistributeTotal } from '@/utils/sampling.util';

@Component({
  components: {
    BaseModal,
  },
})
export default class OneOffSamplingCalculator extends Vue {
  @Prop()
  public initialData!: OneOffCalculateData;
  @Prop()
  public siteArray!: AuditSamplingSite[];
  @Prop()
  public siteName!: string;
  @Prop()
  public criteriaTitle!: string;
  @Prop()
  public auditSiteId!: number;
  @Prop()
  public auditCriteriaId!: number;
  @Prop()
  public samplingMode!: SamplingMode;
  @Prop()
  public criteriaArray!: CplusCriteria[];

  public modalTitle: string = 'Sample Size Calculator';
  public modalSize: string = 'large';
  public calculateData: OneOffCalculateData = {
    confidenceLevel: 95,
    population: 100000,
    margin: 5,
    groupOne: 50,
    recommended: 0,
    output: 0,
  };

  get isDisableInput(): boolean {
    return Number(this.calculateData.output) === 0;
  }

  get displayItems() {
    const dataArray = this.activeDataArray;
    if (!dataArray) {
      return [];
    }

    if (this.samplingMode === SamplingMode.SITE_BASED && this.criteriaArray) {
      return this.criteriaArray.map((criteria, index) => ({
        displayName: criteria.criteriaTitle,
        outputValue: dataArray[index].calculateData.output,
        type: 'criteria',
        index,
      }));
    }

    if (this.samplingMode === SamplingMode.CRITERIA_BASED && this.siteArray) {
      return this.siteArray.map((site, index) => ({
        displayName: site.name,
        outputValue: dataArray[index].calculateData.output,
        type: 'site',
        index,
      }));
    }

    return [];
  }

  get activeDataArray(): (AuditSamplingSite | CplusCriteria)[] | null {
    if (this.samplingMode === SamplingMode.SITE_BASED) {
      return this.calculateData.criteria as CplusCriteria[];
    } else if (this.samplingMode === SamplingMode.CRITERIA_BASED) {
      return this.calculateData.sites as AuditSamplingSite[];
    }
    return null;
  }

  public mounted() {
    if (this.initialData) {
      this.calculateData = _cloneDeep(this.initialData);
    }

    this.calculateSampleSize();
  }

  public updateChildSamplingSize() {
    this.applyDistribution();
  }

  public calculateSampleSize() {
    this.calculateData.output = this.sampleSize(
      Number(this.calculateData.margin),
      Number(this.calculateData.confidenceLevel),
      Number(this.calculateData.groupOne),
      Number(this.calculateData.population),
    );

    this.calculateData.recommended = this.calculateData.output;
    this.applyDistribution();
  }

  public sampleSize(
    margin: number,
    confidence: number,
    response: number,
    population: number,
  ) {
    const pcn = this.probCriticalNormal(confidence / 100.0);
    const d1 = pcn * pcn * response * (100.0 - response);
    const d2 = (population - 1.0) * (margin * margin) + d1;
    if (d2 > 0.0) {
      return Math.ceil((population * d1) / d2);
    }
    return 0;
  }

  public probCriticalNormal(p: number) {
    // input p is confidence level convert it to
    // cumulative probability before computing critical

    let y: number;
    let pr: number;
    let real1: number;
    let real2: number;
    let hold: number;
    let i: number;
    const pn: number[] = [
      0, // ARRAY[1..5] OF REAL
      -0.322232431088,
      -1.0,
      -0.342242088547,
      -0.0204231210245,
      -0.453642210148e-4,
    ];

    const qn: number[] = [
      0, //  ARRAY[1..5] OF REAL
      0.099348462606,
      0.588581570495,
      0.531103462366,
      0.10353775285,
      0.38560700634e-2,
    ];

    pr = 0.5 - p / 2; // one side significance

    if (pr <= 1.0e-8) {
      hold = 6;
    } else {
      if (pr === 0.5) {
        hold = 0;
      } else {
        y = Math.sqrt(Math.log(1.0 / (pr * pr)));
        real1 = pn[5];
        real2 = qn[5];

        for (i = 4; i >= 1; i--) {
          real1 = real1 * y + pn[i];
          real2 = real2 * y + qn[i];
        }

        hold = y + real1 / real2;
      } // end of else pr = 0.5
    } // end of else Pr <= 1.0E-8

    return hold;
  }

  public allowNumericInput(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return; // Allow the event
    }

    // Prevent the event if the key is not a number.
    // Using a regex is more robust than isNaN(Number(key)) which can allow spaces.
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  public handleItemValueChange(event: Event, index: number) {
    const newValue = (event.target as HTMLInputElement).value;
    const dataArray = this.activeDataArray;

    if (dataArray && dataArray[index] && dataArray[index].calculateData) {
      // 1. Update the underlying data model
      dataArray[index].calculateData.output = Number(newValue);
      // 2. Trigger the redistribution logic
      this.applyRedistribution(index);
    }
  }

  public handleApplySampleSize(closeModal: any) {
    let emitData: any = this.calculateData;
    if (this.auditSiteId !== undefined) {
      emitData = {
        ...emitData,
        auditSiteId: this.auditSiteId,
      };
    }

    if (this.auditCriteriaId !== undefined) {
      emitData = {
        ...emitData,
        auditCriteriaId: this.auditCriteriaId,
      };
    }

    if (this.samplingMode === SamplingMode.CRITERIA_BASED && this.siteArray) {
      emitData = {
        ...emitData,
        sites: this.calculateData.sites,
      };
    } else if (
      this.samplingMode === SamplingMode.SITE_BASED &&
      this.criteriaArray
    ) {
      emitData = {
        ...emitData,
        criteria: this.calculateData.criteria,
      };
    }

    this.$emit('applySampleSize', emitData);
    closeModal();
  }

  public applyDistribution() {
    const dataArray = this.activeDataArray;

    if (dataArray && dataArray.length > 0) {
      const newValues = distributeTotal(
        this.calculateData.output,
        dataArray.length,
      );
      dataArray.forEach((item, index) => {
        item.calculateData.output = newValues[index];
      });
    }
  }

  public applyRedistribution(changedIndex: number) {
    const dataArray = this.activeDataArray;

    if (dataArray && dataArray.length > 0) {
      const currentValues = dataArray.map(
        (item) => item.calculateData.output || 0,
      );
      const newValues = redistributeTotal(
        this.calculateData.output,
        currentValues,
        changedIndex,
      );
      dataArray.forEach((item, index) => {
        item.calculateData.output = newValues[index];
      });
    }
  }
}
